import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";

const UserCourseMaterial = () => {
  const [profileId, setProfileId] = useState("");
  const [course, setCourse] = useState("");
  const [subTopics, setSubTopics] = useState("");
  const [description, setDescription] = useState("");
  const [courseOptions, setCourseOptions] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [showNewCourseFields, setShowNewCourseFields] = useState(false);
  const [newCourseName, setNewCourseName] = useState("");
  const [newCourseLogo, setNewCourseLogo] = useState(null);

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (!userId) {
      setErrorMessage("User ID not found. Please log in.");
      return;
    }

    const fetchData = async () => {
      const [profileResult, courseResult] = await Promise.allSettled([
        AxiosInstance.get(`companies/user/profile/${userId}/`),
        AxiosInstance.get("companies/courses/"),
      ]);

      const errors = [];

      if (profileResult.status === "fulfilled") {
        setProfiles(profileResult.value.data);
      } else if (profileResult.reason?.response?.status === 404) {
        setProfiles([]);
        errors.push("No profiles found. Please create a profile first.");
      } else {
        console.error(profileResult.reason);
        errors.push("Error fetching profiles.");
      }

      if (courseResult.status === "fulfilled") {
        setCourseOptions(courseResult.value.data);
      } else {
        console.error(courseResult.reason);
        errors.push("Error fetching courses.");
      }

      setErrorMessage(errors.join(" "));
    };

    fetchData();
  }, [userId]);

  const handleCourseChange = (e) => {
    const selectedValue = e.target.value;
    setCourse(selectedValue);
    setShowNewCourseFields(selectedValue === "other");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      let courseId = course;

      // If "Other" is selected, create new course
      if (showNewCourseFields) {
        if (!newCourseName || !newCourseLogo) {
          setErrorMessage("Please provide course name and logo.");
          setIsLoading(false);
          return;
        }

        const courseFormData = new FormData();
        courseFormData.append("course_name", newCourseName);
        courseFormData.append("course_logo", newCourseLogo);

        const courseResponse = await AxiosInstance.post(
          "companies/course/",
          courseFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        courseId = courseResponse.data.course_id;
      }

      if (!userId || !profileId || !courseId || !subTopics || !description) {
        setErrorMessage("All fields are required.");
        setIsLoading(false);
        return;
      }

      const courseMaterialData = {
        user: parseInt(userId),
        profile: parseInt(profileId),
        course: parseInt(courseId),
        sub_topics: subTopics,
        description,
      };

      const response = await AxiosInstance.post(
        "companies/user/course/materials/",
        courseMaterialData
      );

      if (response.status === 201) {
        alert("Course material added successfully!");
        navigate(-1);
      } else {
        setErrorMessage("Failed to add course material.");
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="modal-backdrop fade show"></div>

      <div
        className="modal d-block"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ backgroundColor: "rgba(0,0,0,0.3)" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Course Material</h5>
            </div>

            <div className="modal-body">
              {errorMessage && (
                <div className="alert alert-danger" role="alert">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Profile:</label>
                  <select
                    className="form-select"
                    value={profileId}
                    onChange={(e) => setProfileId(e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select Profile</option>
                    {profiles.map((profile) => (
                      <option key={profile.profile_id} value={profile.profile_id}>
                        {profile.profile_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Course:</label>
                  <select
                    className="form-select"
                    value={course}
                    onChange={handleCourseChange}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select Course</option>
                    {courseOptions.map((option) => (
                      <option key={option.course_id} value={option.course_id}>
                        {option.course_name}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                </div>

                {showNewCourseFields && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">New Course Name:</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newCourseName}
                        onChange={(e) => setNewCourseName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Course Logo:</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) => setNewCourseLogo(e.target.files[0])}
                        accept="image/*"
                        required
                      />
                    </div>
                  </>
                )}

                <div className="mb-3">
                  <label className="form-label">Topics Covered:</label>
                  <input
                    type="text"
                    className="form-control"
                    value={subTopics}
                    onChange={(e) => setSubTopics(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description:</label>
                  <textarea
                    className="form-control"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="4"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={closeModal}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save Course Material"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserCourseMaterial;
